// readingPoints.js
// ─────────────────────────────────────────────────────────────────────
// Reward system. Reading a story to 95%+ awards POINTS_PER_STORY. Hitting
// REWARD_THRESHOLD unlocks a 20%-off code shown on the profile.
//
// Storage:
//   • localStorage (always)         — instant UI + offline cache + guests
//   • Backend /api/me/reading/*     — authoritative when the user is signed in
//
// When a user is signed in we mirror writes to the backend so any device
// they log into restores the same points / completed slugs / reward code.
// On first sign-in we also push any guest progress accumulated locally
// (one-way merge — backend never decrements).

import axios from 'axios';

export const POINTS_PER_STORY = 10;
export const REWARD_THRESHOLD = 100;
export const REWARD_DESCRIPTION = '20% off your next Dreams League drop';

const KEY_PREFIX = 'dl_reading_points_v1_';
const SYNCED_FLAG = 'dl_reading_synced_v1_';
const userKey = (userId) => `${KEY_PREFIX}${userId || 'guest'}`;
const syncedKey = (userId) => `${SYNCED_FLAG}${userId || 'guest'}`;

const API = process.env.REACT_APP_BACKEND_URL;

const emptyState = () => ({
  points: 0,
  completed: {},     // { [slug]: { ts, title, image, channelLabel, url, points } }
  rewardCode: null,
  rewardClaimed: false,
});

const safeRead = (userId) => {
  try {
    const raw = localStorage.getItem(userKey(userId));
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw);
    return {
      points: Number(parsed.points) || 0,
      completed: (parsed.completed && typeof parsed.completed === 'object') ? parsed.completed : {},
      rewardCode: parsed.rewardCode || null,
      rewardClaimed: !!parsed.rewardClaimed,
    };
  } catch { return emptyState(); }
};

const safeWrite = (userId, state) => {
  try {
    localStorage.setItem(userKey(userId), JSON.stringify(state));
  } catch (e) {
    // Quota exceeded or private mode — non-fatal but worth surfacing in dev.
    if (typeof console !== 'undefined') console.warn('[readingPoints] localStorage write failed:', e);
  }
  try {
    window.dispatchEvent(new CustomEvent('dl:reading-points-changed', { detail: { userId } }));
  } catch (e) {
    if (typeof console !== 'undefined') console.warn('[readingPoints] dispatch event failed:', e);
  }
};

const generateRewardCode = () => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let suffix = '';
  for (let i = 0; i < 4; i += 1) {
    suffix += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return `DREAMS20-${suffix}`;
};

// Convert backend state shape -> local state shape
const fromBackend = (be) => {
  const completed = {};
  (be.completed_list || []).forEach((row) => {
    if (!row?.slug) return;
    completed[row.slug] = {
      ts: row.ts || Date.now(),
      title: row.title || '',
      image: row.image || null,
      channelLabel: row.channel_label || row.channelLabel || '',
      url: row.url || '',
      points: row.points || POINTS_PER_STORY,
    };
  });
  return {
    points: Number(be.points) || 0,
    completed,
    rewardCode: be.reward_code || null,
    rewardClaimed: !!be.reward_claimed,
  };
};

const isSignedIn = (userId) => !!userId && userId !== 'guest';

// Fire-and-forget POST/GET helpers. axios.defaults.withCredentials is set
// globally in /src/index.js so the dl_session cookie rides every request.
const apiGet = (path) => axios.get(`${API}${path}`).then((r) => r.data);
const apiPost = (path, body) => axios.post(`${API}${path}`, body || {}).then((r) => r.data);

/**
 * Pull authoritative state from the backend after sign-in and merge any
 * guest progress we accumulated locally. Safe to call multiple times.
 * Returns the resolved local state (also written to localStorage).
 */
export const hydrateReadingFromBackend = async (userId) => {
  if (!isSignedIn(userId)) return safeRead(userId);
  try {
    // One-time per-user push of any guest/local progress the backend hasn't
    // seen yet. We only push, never pull-then-decrement.
    const alreadySynced = (() => {
      try { return localStorage.getItem(syncedKey(userId)) === '1'; }
      catch { return false; }
    })();

    if (!alreadySynced) {
      const local = safeRead(userId);
      const guest = safeRead('guest');
      const merged = { ...guest.completed, ...local.completed };
      if (Object.keys(merged).length > 0) {
        await apiPost('/api/me/reading/sync', { completed: merged });
      }
      try { localStorage.setItem(syncedKey(userId), '1'); } catch (e) {
        if (typeof console !== 'undefined') console.warn('[readingPoints] syncedKey write failed:', e);
      }
    }

    const be = await apiGet('/api/me/reading');
    const next = fromBackend(be);
    safeWrite(userId, next);
    return next;
  } catch (e) {
    if (typeof console !== 'undefined') console.warn('[readingPoints] hydrate from backend failed:', e);
    return safeRead(userId);
  }
};

/**
 * Mark a story as completed. Idempotent — same slug awards points only once.
 * Writes localStorage immediately for instant UI, then mirrors to backend
 * when signed in. Returns sync result (no awaiting needed by caller).
 */
export const markStoryCompleted = (userId, story) => {
  if (!story?.slug) return { state: safeRead(userId), awarded: false, rewardJustUnlocked: false };
  const state = safeRead(userId);
  if (state.completed[story.slug]) {
    return { state, awarded: false, rewardJustUnlocked: false };
  }
  const wasUnderThreshold = state.points < REWARD_THRESHOLD;
  state.completed[story.slug] = {
    ts: Date.now(),
    title: story.title || '',
    image: story.image || null,
    channelLabel: story.channelLabel || '',
    url: story.url || '',
    points: POINTS_PER_STORY,
  };
  state.points += POINTS_PER_STORY;
  const rewardJustUnlocked = wasUnderThreshold && state.points >= REWARD_THRESHOLD && !state.rewardCode;
  if (rewardJustUnlocked) {
    state.rewardCode = generateRewardCode();
  }
  safeWrite(userId, state);

  // Mirror to backend in the background. Backend is authoritative — on
  // success we re-hydrate so the local reward code matches what the
  // server generated.
  if (isSignedIn(userId)) {
    apiPost('/api/me/reading/complete', {
      slug: story.slug,
      title: story.title || '',
      image: story.image || '',
      channel_label: story.channelLabel || '',
      url: story.url || '',
    })
      .then((be) => {
        const next = fromBackend(be);
        safeWrite(userId, next);
      })
      .catch(() => { /* offline / 401 — localStorage already updated */ });
  }

  return { state, awarded: true, rewardJustUnlocked };
};

export const getStats = (userId) => {
  const state = safeRead(userId);
  const completedList = Object.entries(state.completed)
    .map(([slug, v]) => ({ slug, ...v }))
    .sort((a, b) => (b.ts || 0) - (a.ts || 0));
  // Always derive points from the completed list so the counter never
  // drifts from "stories × 10". If a write to state.points failed at some
  // point in the past (offline, quota, etc.) the chip would otherwise show
  // a stale number — this keeps it self-healing.
  const derivedPoints = completedList.length * POINTS_PER_STORY;
  const points = Math.max(Number(state.points) || 0, derivedPoints);
  return {
    points,
    completedCount: completedList.length,
    completedSlugs: new Set(completedList.map((c) => c.slug)),
    completedList,
    rewardCode: state.rewardCode,
    rewardClaimed: state.rewardClaimed,
    rewardUnlocked: points >= REWARD_THRESHOLD,
    threshold: REWARD_THRESHOLD,
    pointsPerStory: POINTS_PER_STORY,
    rewardDescription: REWARD_DESCRIPTION,
  };
};

export const isStoryCompleted = (userId, slug) => {
  if (!slug) return false;
  return !!safeRead(userId).completed[slug];
};

export const markRewardClaimed = (userId) => {
  const state = safeRead(userId);
  if (!state.rewardCode) return state;
  state.rewardClaimed = true;
  safeWrite(userId, state);
  if (isSignedIn(userId)) {
    apiPost('/api/me/reading/claim-reward').catch(() => { /* offline OK */ });
  }
  return state;
};

// Tiny hook helper for components that need to react to point changes.
export const onPointsChanged = (handler) => {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('dl:reading-points-changed', handler);
  return () => window.removeEventListener('dl:reading-points-changed', handler);
};
